import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../../../apiConfig";
import jsPDF from "jspdf";
import "../../../assets/fonts/kids_avenue/KidsAvenue";
import greatVibes from "../../../fonts/GreatVibes";
import statesAndDistricts from "./../../../data/statesAndDistricts.json";

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const CorrectionPanApplication = () => {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [editId, setEditId] = useState(null);

  // Helper to parse various date formats from server into YYYY-MM-DD
  const parseDateForInput = (dateStr) => {
    if (!dateStr) return "";
    // If it's already YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.split("T")[0];

    // If it's DD/MM/YYYY
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    try {
      const date = new Date(dateStr);
      return !isNaN(date) ? date.toISOString().split("T")[0] : "";
    } catch { return ""; }
  };

  // Load existing record if editing
  useEffect(() => {
    const id = localStorage.getItem("correctionPanId");
    if (id && id !== "undefined") {
      setEditId(id);
      const authToken = localStorage.getItem("authToken");
      fetch(`${API_BASE_URL}/CorrectionPan/GetCorrectionPanById/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Fetched Correction PAN Data:", data);
          setFormData({
            PanNo: data.panNo || "",
            category: data.applicantType || "",
            ApplicantCheckedOption: data.applicantCheckedOption || "",
            LastName: data.lastName || "",
            FirstName: data.firstName || "",
            MiddleName: data.middleName || "",
            NewPanName: data.newPanName || "",
            FatherLastName: data.fatherLastName || "",
            FatherFirstName: data.fatherFirstName || "",
            FatherMiddleName: data.fatherMiddleName || "",
            MotherLastName: data.motherLastName || "",
            MotherFirstName: data.motherFirstName || "",
            MotherMiddleName: data.motherMiddleName || "",
            Dob: parseDateForInput(data.date),
            ISDCode: data.isdCode || "",
            STDCode: data.stdCode || "",
            MobileNo: data.mobileNo || "",
            EmailId: data.emailId || "",
            AadharName: data.aadharName || "",
            PanDispatchState: data.panDispatchState || "",
            Aadhar_EID: data.aadhaR_EID || data.aadhar_EID || "",
            IdentityProof: data.identityProof || "AadhaarCard",
            AddressProof: data.addressProof || "AadhaarCard",
            DobProof: data.dobProof || "AadhaarCard",
            ResidentRoom: data.residentRoom || "",
            ResidentPremises: data.residentPremises || "",
            ResidentStreet: data.residentStreet || "",
            ResidentArea: data.residentArea || "",
            ResidentCity: data.residentCity || "",
            ResidentState: data.residentState || "",
            ResidentPinCode: data.residentPinCode || "",
            pdfDocument: null,
            photo: null,
            signature: null,
            ChangeResoneCheckedOption: data.changeResoneCheckedOption ? data.changeResoneCheckedOption.split(",") : [],
          });
        })
        .catch((err) => console.error("Error loading record:", err));
    }
  }, []);

  const [formData, setFormData] = useState({
    AreaCode: "",
    PanNo: "",
    category: "",
    ApplicantCheckedOption: "",
    LastName: "",
    FirstName: "",
    MiddleName: "",
    NewPanName: "",
    FatherLastName: "",
    FatherFirstName: "",
    FatherMiddleName: "",
    MotherLastName: "",
    MotherFirstName: "",
    MotherMiddleName: "",
    Dob: "",
    ISDCode: "",
    STDCode: "",
    MobileNo: "",
    EmailId: "",
    AadharName: "",
    PanDispatchState: "",
    Aadhar_EID: "",
    //RA_Address: "",
    IdentityProof: "",
    AddressProof: "",
    DobProof: "",
    ResidentRoom: "",
    ResidentPremises: "",
    ResidentStreet: "",
    ResidentArea: "",
    ResidentCity: "",
    ResidentState: "",
    ResidentPinCode: "",
    OfficeName: "",
    OfficeRoom: "",
    OfficePremises: "",
    OfficeStreet: "",
    OfficeArea: "",
    OfficeCity: "",
    OfficeState: "",
    OfficePinCode: "",
    pdfDocument: null,  // File
    photo: null,        // File
    signature: null,    // File
  });

  const pdfDocumentRef = useRef(null);
  const photoRef = useRef(null);
  const signatureRef = useRef(null);
  const [submissionDate, setSubmissionDate] = useState(null);

  const formatDateToString = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = d.getFullYear();
    return `${day}/${month}/${year}`; // Format: dd/MM/yyyy
  }

  const [errors, setErrors] = useState({
    Aadhar_EID: "",
    PanNo: "",
  });

  const aadharRegex = /^[2-9]{1}[0-9]{11}$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

  // Register the optional Great Vibes font with jsPDF (requires base64 in src/fonts/GreatVibes.js)
  const registerGreatVibes = (doc) => {
    const fontString = (greatVibes || "").trim();
    if (!fontString || fontString.includes("PASTE_YOUR_BASE64_STRING_HERE")) {
      return false; // Skip when no base64 is provided
    }

    doc.addFileToVFS("GreatVibes-Regular.ttf", fontString);
    doc.addFont("GreatVibes-Regular.ttf", "GreatVibes", "normal");
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Pincode limit to 6 digits
    if (name === "ResidentPinCode" || name === "OfficePinCode" || name === "RepresentativePinCode") {
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 6) return; // block after 6 digits
      setFormData({ ...formData, [name]: digitsOnly });
      return;
    }

    if (name === "Aadhar_EID") {
      // Allow only digits
      const digitsOnly = value.replace(/\D/g, "");
      if (digitsOnly.length > 12) return; // Prevent more than 12 digits

      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));

      if (digitsOnly.length === 12 && !aadharRegex.test(digitsOnly)) {
        setErrors((prev) => ({ ...prev, Aadhar_EID: "Enter valid 12-digit Aadhaar number." }));
      } else {
        setErrors((prev) => ({ ...prev, Aadhar_EID: "" }));
      }
    }

    else if (name === "PanNo") {
      const pan = value.toUpperCase();
      setFormData((prev) => ({ ...prev, [name]: pan }));

      if (pan.length === 10 && !panRegex.test(pan)) {
        setErrors((prev) => ({ ...prev, PanNo: "Enter valid PAN number (ABCDE1234F)." }));
      } else {
        setErrors((prev) => ({ ...prev, PanNo: "" }));
      }
    }

    else if (name === "AadharName") {
      const words = value.split(" ").filter((w) => w.trim() !== "");

      setFormData((prev) => ({
        ...prev,
        [name]: value.toUpperCase(),
        FirstName: (words[0] || "").toUpperCase(),
        MiddleName: (words[1] || "").toUpperCase(),
        LastName: (words[2] || "").toUpperCase(),
      }));
    }

    else if (name === "PanDispatchState") {
      setFormData((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    }

    else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleMultipleCheckboxChange = (event) => {
    const { name, checked } = event.target;

    setFormData((prevFormData) => {
      const updatedCheckedOptions = checked
        ? [...(prevFormData.ChangeResoneCheckedOption || []), name] // Add if checked
        : (prevFormData.ChangeResoneCheckedOption || []).filter(item => item !== name); // Remove if unchecked

      return {
        ...prevFormData,
        ChangeResoneCheckedOption: updatedCheckedOptions, // Store as array
      };
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    // Log the selected file
    console.log(`Selected file for ${name}:`, file);

    // Validation based on input name
    if (file) {
      const fileType = file.type;

      // PDF validation
      if (name === "pdfDocument" && fileType !== "application/pdf") {
        alert("Only PDF files are allowed.");
        e.target.value = ""; // Clear input
        return;
      }

      // Image validation for photo and signature
      if ((name === "photo" || name === "signature") &&
        !(fileType === "image/jpeg" || fileType === "image/png")) {
        alert("Only JPG and PNG files are allowed.");
        e.target.value = ""; // Clear input
        return;
      }
    }

    // If valid, update state
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: file,
    }));
  };


  const [disableNames, setDisableNames] = useState(false);
  const handleCategoryChange = (e) => {
    const selectedOption = e.target.value;
    setFormData((prev) => ({ ...prev, category: selectedOption }));
    const disableOptions = [
      "Trust",
      "Hindu Undivided Family",
      "Partnership Firm",
      "Association Of Person",
      "Body Of Individuals Individuals",
    ];
    setDisableNames(disableOptions.includes(selectedOption));
  };


  // Replace form onSubmit with this handler
  const handleFormSubmit = (event) => {
    event.preventDefault();
    setShowConfirmModal(true);
  };

  // Confirm modal handlers
  const handleConfirm = async () => {
    // Simple validation example (add more as needed)
    if (
      !formData.PanNo ||
      !formData.ApplicantCheckedOption ||
      !formData.LastName ||
      !formData.NewPanName ||
      !formData.MobileNo ||
      !formData.EmailId ||
      !formData.AadharName ||
      !formData.Aadhar_EID
    ) {
      alert("Please fill all required fields before submitting.");
      setShowConfirmModal(false);
      return;
    }
    setShowConfirmModal(false);
    await handleSubmitCorrectionPan();
  };

  const handleStay = () => {
    setShowConfirmModal(false);
  };


  const handleSubmitCorrectionPan = async (event) => {
    if (event) event.preventDefault();
    const photoFile = formData.photo;
    const signatureFile = formData.signature;
    let backendSubmissionDate = "";

    try {
      const applicationFormData = new FormData();
      applicationFormData.append("PanNo", formData.PanNo);
      //applicationFormData.append("StatusOfApplicant", formData.StatusOfApplicant);
      applicationFormData.append("ApplicantType", formData.category);
      applicationFormData.append("ApplicantCheckedOption", formData.ApplicantCheckedOption);
      applicationFormData.append("LastName", formData.LastName);
      applicationFormData.append("FirstName", formData.FirstName);
      applicationFormData.append("MiddleName", formData.MiddleName);
      applicationFormData.append("NewPanName", formData.NewPanName);
      applicationFormData.append("FatherLastName", formData.FatherLastName);
      applicationFormData.append("FatherFirstName", formData.FatherFirstName);
      applicationFormData.append("FatherMiddleName", formData.FatherMiddleName);
      applicationFormData.append("MotherLastName", formData.MotherLastName);
      applicationFormData.append("MotherFirstName", formData.MotherFirstName);
      applicationFormData.append("MotherMiddleName", formData.MotherMiddleName);
      applicationFormData.append("Date", formatDateToString(formData.Dob));
      applicationFormData.append("ISDCode", formData.ISDCode);
      applicationFormData.append("STDCode", formData.STDCode);
      applicationFormData.append("MobileNo", formData.MobileNo);
      applicationFormData.append("EmailId", formData.EmailId);
      applicationFormData.append("AadharName", formData.AadharName);
      applicationFormData.append("PanDispatchState", formData.PanDispatchState);
      applicationFormData.append("Aadhar_EID", formData.Aadhar_EID);
      //applicationFormData.append("RA_Address", formData.RA_Address);
      applicationFormData.append("IdentityProof", "AadhaarCard");
      applicationFormData.append("AddressProof", "AadhaarCard");
      applicationFormData.append("DobProof", "AadhaarCard");
      applicationFormData.append("ChangeResoneCheckedOption", formData.ChangeResoneCheckedOption);
      applicationFormData.append("ResidentRoom", formData.ResidentRoom);
      applicationFormData.append("ResidentPremises", formData.ResidentPremises);
      applicationFormData.append("ResidentStreet", formData.ResidentStreet);
      applicationFormData.append("ResidentArea", formData.ResidentArea);
      applicationFormData.append("ResidentCity", formData.ResidentCity);
      applicationFormData.append("ResidentState", formData.ResidentState);
      applicationFormData.append("ResidentPinCode", formData.ResidentPinCode);
      // applicationFormData.append("OfficeName", formData.OfficeName);
      // applicationFormData.append("OfficeRoom", formData.OfficeRoom);
      // applicationFormData.append("OfficePremises", formData.OfficePremises);
      // applicationFormData.append("OfficeStreet", formData.OfficeStreet);
      // applicationFormData.append("OfficeArea", formData.OfficeArea);
      // applicationFormData.append("OfficeCity", formData.OfficeCity);
      // applicationFormData.append("OfficeState", formData.OfficeState);
      // applicationFormData.append("OfficePinCode", formData.OfficePinCode);
      applicationFormData.append("PdfDocument", formData.pdfDocument);
      applicationFormData.append("Photo", formData.photo);
      applicationFormData.append("Signature", formData.signature);

      const authToken = localStorage.getItem("authToken");

      console.log("Submitting FormData to API...");
      // ✅ Debugging - Log all FormData entries
      for (let pair of applicationFormData.entries()) {
        console.log(pair[0], pair[1], pair[1] instanceof File ? pair[1].name : "");
      }

      const url = editId
        ? `${API_BASE_URL}/CorrectionPan/Update/${editId}`
        : `${API_BASE_URL}/CorrectionPan/CorrectionPanApplication`;

      const response = await fetch(url, {
        method: editId ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: applicationFormData
      });

      let result;
      const responseText = await response.text();
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        console.error("Failed to parse JSON response:", responseText);
        throw new Error("Server returned an invalid response. Please check the logs.");
      }

      if (!response.ok) {
        const errorMessage = result.message || result.Message || "Failed to submit application data.";
        alert(errorMessage);
        return;
      }

      backendSubmissionDate = result.submissionDate || "";
      setSubmissionDate(result.submissionDate);

      console.log("Application Processed:", result);
      // Trigger balance update
      window.dispatchEvent(new Event("balanceUpdated"));

      setFormData({
        AreaCode: "",
        PanNo: "",
        category: "",
        ApplicantCheckedOption: "",
        LastName: "",
        FirstName: "",
        MiddleName: "",
        NewPanName: "",
        FatherLastName: "",
        FatherFirstName: "",
        FatherMiddleName: "",
        MotherLastName: "",
        MotherFirstName: "",
        MotherMiddleName: "",
        Dob: "",
        ISDCode: "",
        STDCode: "",
        MobileNo: "",
        EmailId: "",
        AadharName: "",
        PanDispatchState: "",
        Aadhar_EID: "",
        IdentityProof: "",
        AddressProof: "",
        DobProof: "",
        ResidentRoom: "",
        ResidentPremises: "",
        ResidentStreet: "",
        ResidentArea: "",
        ResidentCity: "",
        ResidentState: "",
        ResidentPinCode: "",
        pdfDocument: null,  // File
        photo: null,        // File
        signature: null,    // File
      });

      // Reset actual file inputs
      if (pdfDocumentRef.current) pdfDocumentRef.current.value = "";
      if (signatureRef.current) signatureRef.current.value = "";

      alert(editId ? "Form updated successfully!" : "Form submitted successfully!");

      if (editId) {
        localStorage.removeItem("correctionPanId");
      }
      navigate("/dashboard/correction-pan-application-report");

    } catch (error) {
      console.error("While submitting form something went wrong. Please try again.");
      if (typeof error.message === "string") {
        alert(error.message);
      } else {
        alert("An unexpected error occurred. Please try again.");
      }
    };

    const doc = new jsPDF();

    // Convert images to base64
    let photoBase64 = null;
    let signatureBase64 = null;

    if (photoFile) {
      photoBase64 = await readFileAsBase64(photoFile);
    }
    if (signatureFile) {
      signatureBase64 = await readFileAsBase64(signatureFile);
    }

    const hasGreatVibes = registerGreatVibes(doc);

    // Draw a border around the entire PDF
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    doc.setDrawColor(0, 0, 0); // Black border
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10); // Outer border (5px margin)

    // Title
    // if (hasGreatVibes) {
    //   doc.setFont("GreatVibes", "normal");
    //   doc.setFontSize(16);
    // } else {
    //   doc.setFont("helvetica", "bold");
    //   doc.setFontSize(10);
    // }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8); // Set a smaller font size for the title
    const title = "Request For New PAN Card Or/ And Changes Or Correction in PAN Data";
    const titleWidth = doc.getTextWidth(title);
    const centerX = (pageWidth - titleWidth) / 2;
    doc.text(title, centerX, 10);
    //doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    // Adjusted Y position for boxes (move them up slightly)
    const boxYnn = 7; // New Y position for both image boxes

    // Left Image Box
    doc.rect(10, boxYnn, 30, 35); // Adjusted Left image box (moved slightly up)
    if (photoBase64) {
      doc.addImage(photoBase64, "JPEG", 10, boxYnn, 30, 35);
    }

    // Right Image Box
    doc.rect(pageWidth - 40, boxYnn, 30, 35); // Right image box
    if (photoBase64) {
      doc.addImage(photoBase64, "JPEG", pageWidth - 40, boxYnn, 30, 35);
    }

    // Text inside Left Image Placeholder
    doc.setFontSize(6); // Set font size for better readability
    const placeholderText = [
      "Only ‘Individuals’ to affix",
      "recent photograph",
      "(3.5 cm × 2.5 cm)"
    ];

    let textY = boxYnn + 10; // Adjusted starting Y position for text inside the left box (move up slightly)
    if (!photoBase64) {
      placeholderText.forEach((line) => {
        const textWidth = doc.getTextWidth(line);
        const textX = 10 + (30 - textWidth) / 2; // Center text in the 30px wide box
        doc.text(line, textX, textY);
        textY += 3; // Increment Y position for the next line
      });
    }

    doc.setFontSize(5); // Set font size for better readability
    const placeholderText2 = [
      "Signature/Left thumb",
      "impression across this photo"
    ];

    // Position of the horizontal line
    const horizontalLineY = boxYnn + 28;  // Adjusted Y position for the horizontal line (move up slightly)
    const horizontalLineStartX = 10;  // Starting X position for the line (left side of the box)
    const horizontalLineEndX = 40;   // Ending X position for the line (right side of the box)

    // Draw the horizontal line above the text
    doc.line(horizontalLineStartX, horizontalLineY, horizontalLineEndX, horizontalLineY); // Draw the line

    let textYn = boxYnn + 31; // Adjusted starting Y position for text inside the box (move up slightly)
    if (!photoBase64) {
      placeholderText2.forEach((line) => {
        const textWidth = doc.getTextWidth(line);
        const textX = 10 + (30 - textWidth) / 2; // Center text in the 30px wide box
        doc.text(line, textX, textYn);
        textYn += 3; // Increment Y position for the next line
      });
    }

    // Repeat for Right Image Placeholder
    textY = boxYnn + 15; // Reset adjusted Y position for right box
    if (!photoBase64) {
      placeholderText.forEach((line) => {
        const textWidth = doc.getTextWidth(line);
        const textX = pageWidth - 40 + (30 - textWidth) / 2; // Center text in the 30px wide box
        doc.text(line, textX, textY);
        textY += 3; // Increment Y position for the next line
      });
    }

    doc.setFontSize(8);
    const panText = "Permanent Account Number (PAN):";
    const panTextWidth = doc.getTextWidth(panText);
    const panCenterX = (pageWidth - panTextWidth) / 2; // Center the text
    doc.text(panText, panCenterX, 29);

    // Draw 10 small centered boxes for PAN
    const boxCount = 10; // Number of boxes
    const boxSize = 5; // Width and height of each box
    const totalWidth = boxCount * boxSize + (boxCount - 1) * 0; // Total width of the boxes row
    const startX = (pageWidth - totalWidth) / 2; // Center the boxes horizontally
    const startY = 31; // Starting y position

    // Ensure PAN number fits into 10 boxes (padding or truncation)
    const panNumber = (formData.PanNo || "").toString().padEnd(10, " ").substring(0, 10);

    for (let i = 0; i < boxCount; i++) {
      doc.rect(startX + i * (boxSize + 0), startY, boxSize, boxSize); // Draw each box
      doc.setFont("KidsAvenue", "normal");
      doc.text(panNumber[i], startX + i * (boxSize + 0) + boxSize / 2 - 1, startY + boxSize / 2 + 2);
      doc.setFont("helvetica", "normal");
    }

    // Instructions with centered text
    doc.setFontSize(8);
    const instructionsText = "Please read Instructions ‘h’ & ‘i’ for selecting boxes on left margin of this form.";
    const instructionsWidth = doc.getTextWidth(instructionsText);
    const instructionsCenterX = (pageWidth - instructionsWidth) / 2; // Center the text horizontally
    doc.text(instructionsText, instructionsCenterX, 40);

    // Box properties
    const boxWidth = 50;  // Width of the box
    const boxHeight = 15; // Height of the box
    const boxX = pageWidth - 100 + (130 - boxWidth) / 2; // Center the box horizontally
    const boxY = textYn + 1; // Position the box below the previous content

    // Draw the box at calculated position
    doc.rect(boxX, 44, boxWidth, boxHeight);
    if (signatureBase64) {
      doc.addImage(signatureBase64, "JPEG", boxX + 1, 44 + 1, boxWidth - 2, boxHeight - 2);
    }

    // Text properties
    const signatureText = "Signature/Left thumb impression across this photo";
    doc.setFontSize(5); // Set font size for the text

    // Calculate text dimensions
    const textWidth_sig = doc.getTextWidth(signatureText); // Get the width of the text
    const textHeight_sig = 1 * 0.15; // Approximate height (5% of font size is standard)

    // Adjust text position for bottom alignment within the box
    const textXright_sig = boxX + (boxWidth - textWidth_sig) / 2; // Center horizontally
    const textYright_sig = boxY + boxHeight - textHeight_sig - 2; // Position near the bottom (2 units padding)

    if (!signatureBase64) {
      // Draw the text inside the box
      doc.text(signatureText, textXright_sig, textYright_sig);
    }
    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("1. Full Name (Full expanded name to be mentioned as appearing in proof of identity/address\n documents: initials are not permitted", 10, 47);
    // Lease Select Title Section Below Full Name
    const leaseSelectTitleX = 20; // Horizontal position of the title
    const leaseSelectTitleY = 55; // Position for the Lease Select Title (below the Full Name section)
    // Draw the "Lease Select Title"
    doc.setFontSize(8); // Set font size for the title
    doc.text("Lease Select Title", 10, 56);

    // Define the starting X and Y position for checkboxes (aligned with the title)
    const checkboxStartX = leaseSelectTitleX + 15; // Starting X position for checkboxes (offset to the right of the title)
    const checkboxY = leaseSelectTitleY - 2; // Adjust Y to align with the title text

    // Size of the checkbox
    const checkboxSize = 4; // Size of the checkbox

    // Offsets for each checkbox (ensure they are placed continuously)
    const checkboxSpacing = checkboxSize + 20; // Spacing between checkboxes (includes space for labels)

    // Function to draw a right tick ✔
    const drawCheckmark = (x, y) => {
      doc.line(x, y + 2, x + 2, y + 4); // bottom line of checkmark
      doc.line(x + 2, y + 4, x + 6, y); // upward slant of checkmark
    };

    // Draw the first checkbox ("as applicable")
    doc.rect(checkboxStartX, checkboxY, checkboxSize, checkboxSize); // Draw the checkbox (empty box)
    doc.text("as applicable", checkboxStartX + checkboxSize + 1, checkboxY + 3); // Label for "as applicable"
    if (formData.ApplicantCheckedOption === "as applicable") {
      drawCheckmark(checkboxStartX, checkboxY);
    }
    // Draw the second checkbox ("Shri")
    const checkboxX2 = checkboxStartX + checkboxSpacing; // Offset X position for next checkbox
    doc.rect(checkboxX2, checkboxY, checkboxSize, checkboxSize); // Draw the checkbox (empty box)
    doc.text("Shri", checkboxX2 + checkboxSize + 1, checkboxY + 3); // Label for "Shri"
    if (formData.ApplicantCheckedOption === "Shri") {
      drawCheckmark(checkboxX2, checkboxY);
    }
    // Draw the third checkbox ("Smt")
    const checkboxX3 = checkboxX2 + checkboxSpacing; // Further offset for the next checkbox
    doc.rect(checkboxX3, checkboxY, checkboxSize, checkboxSize); // Draw the checkbox (empty box)
    doc.text("Smt", checkboxX3 + checkboxSize + 1, checkboxY + 3); // Label for "Smt"
    if (formData.ApplicantCheckedOption === "Smt") {
      drawCheckmark(checkboxX3, checkboxY);
    }

    // Draw the fourth checkbox ("Kumari")
    const checkboxX4 = checkboxX3 + checkboxSpacing; // Further offset for the next checkbox
    doc.rect(checkboxX4, checkboxY, checkboxSize, checkboxSize); // Draw the checkbox (empty box)
    doc.text("Kumari", checkboxX4 + checkboxSize + 1, checkboxY + 3); // Label for "Kumari"
    if (formData.ApplicantCheckedOption === "Kumari") {
      drawCheckmark(checkboxX4, checkboxY);
    }

    // Draw the fifth checkbox ("M/S")
    const checkboxX5 = checkboxX4 + checkboxSpacing; // Further offset for the next checkbox
    doc.rect(checkboxX5, checkboxY, checkboxSize, checkboxSize); // Draw the checkbox (empty box)
    doc.text("M/S", checkboxX5 + checkboxSize + 1, checkboxY + 3); // Label for "M/S"
    if (formData.ApplicantCheckedOption === "M/S") {
      drawCheckmark(checkboxX5, checkboxY);
    }


    // Function to draw a title and a set of aligned boxes
    function drawTitleAndAlignedBoxes(title, titleX, titleY, boxX, boxY, boxCountn, boxWidthn, boxHeightn, inputText, doc, fontSize = 10,
      fontWeight = "normal", yOffset = 0) {
      if (!doc) {
        console.error("jsPDF instance (doc) is undefined!");
        return;
      }
      // Draw the title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8); // Set font size for the title
      doc.text(title, titleX, titleY);

      // Convert inputText to a string safely
      const letters = String(inputText || "").substring(0, boxCountn).split("");

      // Set font and font size for box content
      doc.setFont("KidsAvenue", fontWeight);
      doc.setFontSize(fontSize);

      for (let i = 0; i < boxCountn; i++) {
        const currentX = boxX + i * boxWidthn; // Spacing between boxes
        doc.rect(currentX, boxY, boxWidthn, boxHeightn); // Draw a box

        if (letters[i]) {
          const textX = currentX + boxWidthn / 2 - 1.5;
          const textY = boxY + boxHeightn / 2 + fontSize / 2 - 1 + yOffset;
          doc.text(letters[i], textX, textY); // Adjust for centering
        }
      }
      doc.setFont("helvetica", "normal");
    }

    // Shared variables for box dimensions and alignment
    const boxCountn = 32; // Number of boxes
    const boxWidthn = 5; // Width of each box
    const boxHeightn = 5; // Height of each box
    const boxesStartX = 40; // Fixed starting X position for all boxes
    const titleStartX = 10; // X position for titles

    // ✅ Use dynamic formData values
    // const lastName = formData.LastName || "";
    // const firstName = formData.FirstName || "";
    // const middleName = formData.MiddleName || "";

    // Draw the "Last Name / Surname" title and boxes
    drawTitleAndAlignedBoxes(
      "Last Name / Surname",
      titleStartX,
      62, // Title Y position
      boxesStartX,
      60, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.LastName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "First Name" title and boxes
    drawTitleAndAlignedBoxes(
      "First Name",
      titleStartX,
      67, // Title Y position
      boxesStartX,
      65, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.FirstName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Middle Name",
      titleStartX,
      73, // Title Y position
      boxesStartX,
      70, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.MiddleName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Function to draw the title and a set of aligned boxes
    function drawTitleAndBoxes(title, titleX, titleY, boxX, boxY, boxCount, boxWidth, boxHeight, text, doc, fontSize = 10,
      fontWeight = "normal", yOffset = 0) {
      if (!doc) {
        console.error("jsPDF instance (doc) is undefined!");
        return;
      }
      // Draw the title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8); // Set font size for the title
      doc.text(title, titleX, titleY);

      // Ensure text is safely converted to string and sliced to match boxCount
      const letters = String(text || "").substring(0, boxCount).split("");

      // Set font and font size for box content
      doc.setFont("KidsAvenue", fontWeight);
      doc.setFontSize(fontSize);

      // Draw the boxes and insert letters
      for (let i = 0; i < boxCount; i++) {
        const currentX = boxX + i * boxWidth;
        doc.rect(currentX, boxY, boxWidth, boxHeight); // Draw the box

        // Add letter to the center of the box (if exists)
        if (letters[i]) {
          const textX = currentX + boxWidth / 2 - 1.5;
          const textY = boxY + boxHeight / 2 + fontSize / 2 - 1 + yOffset;
          doc.text(letters[i], textX, textY); // Center letter
        }
      }
      doc.setFont("helvetica", "normal");
    }

    // Shared variables for box dimensions and alignment
    const boxCountPan = 38; // Number of boxes (on each line)
    const boxesStartXPan = 10; // Fixed starting X position for all boxes

    // Draw the title "Name you would like it printed on the PAN card"
    drawTitleAndBoxes(
      "Name you would like it printed on the PAN card",
      titleStartX,
      78, // Title Y position
      boxesStartXPan,
      80, // Boxes Y position (slightly below the title)
      boxCountPan,
      boxWidthn,
      boxHeightn,
      String(formData.NewPanName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the first row of 37 boxes
    drawTitleAndBoxes(
      "",
      0,
      0, // No title Y position
      boxesStartXPan,
      80 + boxHeightn, // Align directly below the first row of boxes
      boxCountPan,
      boxWidthn,
      boxHeightn,
      "",
      doc,
      8,
      "normal",
      -2
    );

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("2. Details of Parents (applicable only for individual applicants),\n Father’s Name : (Mandatory, Even married women should fill in father’s name only)", 10, 93);

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Last Name / Surname",
      titleStartX,
      101, // Title Y position
      boxesStartX,
      98, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.FatherLastName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "First Name",
      titleStartX,
      106, // Title Y position
      boxesStartX,
      103, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.FatherFirstName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Middle/Mother’s Name",
      titleStartX,
      111, // Title Y position
      boxesStartX,
      108, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.FatherMiddleName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Last Name / Surname",
      titleStartX,
      117, // Title Y position
      boxesStartX,
      113, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.MotherLastName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "First Name",
      titleStartX,
      122, // Title Y position
      boxesStartX,
      118, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.MotherFirstName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Middle Name",
      titleStartX,
      126, // Title Y position
      boxesStartX,
      123, // Boxes Y position
      boxCountn,
      boxWidthn,
      boxHeightn,
      String(formData.MotherMiddleName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "Select the name of either father or mother which you may like to be printed on PAN card (select one only)\n" +
      "(In case no option is provided then PAN card will be Issued with father’s name)",
      10,
      130
    );

    // Define the positions and sizes for the checkboxes
    const checkboxYn = 131; // Y position for the checkboxes
    const labelYOffset = checkboxSize - 1; // Offset to align the label text with the checkbox
    const labelXOffset = 2; // Offset for the label text from the checkbox

    // Draw the "Father's Name" checkbox and label
    const fatherCheckboxX = 135; // X position for the Father's Name checkbox
    doc.rect(fatherCheckboxX, checkboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Father’s Name",
      fatherCheckboxX + checkboxSize + labelXOffset,
      checkboxYn + labelYOffset
    ); // Draw the label, aligned with the checkbox

    // Draw the "Mother's Name" checkbox and label
    const motherCheckboxX = 170; // X position for the Mother's Name checkbox
    doc.rect(motherCheckboxX, checkboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Mother’s Name",
      motherCheckboxX + checkboxSize + labelXOffset,
      checkboxYn + labelYOffset
    ); // Draw the label, aligned with the checkbox

    // Auto-tick "Father's name"
    drawCheckmark(fatherCheckboxX, checkboxYn);

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "3. Date of Birth/Incorporation/Agreement/Partnership/Trust Deed/\n" +
      "Formation of Body of individuals or Association of Persons",
      10,
      139
    );

    const rawDob = String(formatDateToString(formData.Dob) || "");
    const dob = rawDob.replace(/\//g, ""); // Remove all slashes

    const day = dob.slice(0, 2);   // "DD"
    const month = dob.slice(2, 4); // "MM"
    const year = dob.slice(4, 8);  // "YYYY"

    // Shared dimensions and spacing
    const DatelabelYOffset = -2; // Offset to place labels above the boxes
    const DatestartX = 120; // Starting X position for the first label/box group
    const DatestartY = 140; // Starting Y position for the boxes
    const boxSpacing = 5; // Space between each label/box group

    // Draw "Day" label and boxes
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Day", DatestartX, DatestartY + DatelabelYOffset); // Draw the "Day" label above the boxes
    for (let i = 0; i < 2; i++) {
      doc.rect(DatestartX + i * (boxWidthn + 2), DatestartY, boxWidthn, boxHeightn);
      if (day[i]) {
        doc.setFont("KidsAvenue", "normal");
        doc.text(day[i], DatestartX + i * (boxWidthn + 2) + 2, DatestartY + 4);
        doc.setFont("helvetica", "normal");
      }
    }

    // Draw "Month" label and boxes
    const MonthStartX = DatestartX + 2 * boxWidthn + boxSpacing; // X position for "Month"
    doc.text("Month", MonthStartX, DatestartY + DatelabelYOffset); // Draw the "Month" label above the boxes
    for (let i = 0; i < 2; i++) {
      doc.rect(MonthStartX + i * (boxWidthn + 2), DatestartY, boxWidthn, boxHeightn);
      if (month[i]) {
        doc.setFont("KidsAvenue", "normal");
        doc.text(month[i], MonthStartX + i * (boxWidthn + 2) + 2, DatestartY + 4);
        doc.setFont("helvetica", "normal");
      }
    }

    // Draw "Year" label and boxes
    const YearStartX = MonthStartX + 2 * boxWidthn + boxSpacing; // X position for "Year"
    doc.text("Year", YearStartX, DatestartY + DatelabelYOffset); // Draw the "Year" label above the boxes
    for (let i = 0; i < 4; i++) {
      doc.rect(YearStartX + i * (boxWidthn + 2), DatestartY, boxWidthn, boxHeightn);
      if (year[i]) {
        doc.setFont("KidsAvenue", "normal");
        doc.text(year[i], YearStartX + i * (boxWidthn + 2) + 2, DatestartY + 4);
        doc.setFont("helvetica", "normal");
      }
    }

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "4. Gender (for ‘Individual’ applicant only)",
      10,
      149
    );

    // Define the positions and sizes for the checkboxes
    const GendercheckboxYn = 146; // Y position for the checkboxes
    const GenderlabelYOffset = checkboxSize - 1; // Offset to align the label text with the checkbox
    const GenderlabelXOffset = 2; // Offset for the label text from the checkbox

    // Draw the "Male" checkbox and label
    const MaleCheckboxX = 75; // X position for the Male checkbox
    doc.rect(MaleCheckboxX, GendercheckboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Male",
      MaleCheckboxX + checkboxSize + GenderlabelXOffset,
      GendercheckboxYn + GenderlabelYOffset
    ); // Draw the label, aligned with the checkbox

    // Draw the "female" checkbox and label
    const FemlaeCheckboxX = 95; // X position for thefemale checkbox
    doc.rect(FemlaeCheckboxX, GendercheckboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Female",
      FemlaeCheckboxX + checkboxSize + GenderlabelXOffset,
      GendercheckboxYn + GenderlabelYOffset
    ); // Draw the label, aligned with the checkbox

    // Draw the "Transgender" checkbox and label
    const transgenderCheckboxX = 120; // X position for the Transgender checkbox
    doc.rect(transgenderCheckboxX, GendercheckboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Transgender",
      transgenderCheckboxX + checkboxSize + GenderlabelXOffset,
      GendercheckboxYn + GenderlabelYOffset
    ); // Draw the label, aligned with the checkbox

    // Draw the "Photo Mismatch" checkbox and label
    const PhotoCheckboxX = 150; // X position for the Photo Mismatch checkbox
    doc.rect(PhotoCheckboxX, GendercheckboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "5. Photo Mismatch",
      PhotoCheckboxX + checkboxSize + GenderlabelXOffset,
      GendercheckboxYn + GenderlabelYOffset
    ); // Draw the label, aligned with the checkbox

    // Auto-select Gender based on Title
    if (formData.ApplicantCheckedOption === "Shri") {
      drawCheckmark(MaleCheckboxX, GendercheckboxYn);
    } else if (
      formData.ApplicantCheckedOption === "Smt" ||
      formData.ApplicantCheckedOption === "Kumari" ||
      formData.ApplicantCheckedOption === "M/S"
    ) {
      drawCheckmark(FemlaeCheckboxX, GendercheckboxYn);
    }

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "6. Signature Mismatch ",
      10,
      155
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "7. Address for Communication",
      50,
      155
    );
    const locationcheckboxYn = 152; // Y position for the checkboxes
    // Draw the "Father's Name" checkbox and label
    const resiCheckboxX = 100; // X position for the Father's Name checkbox
    doc.rect(resiCheckboxX, locationcheckboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Residence",
      resiCheckboxX + checkboxSize + labelXOffset,
      locationcheckboxYn + labelYOffset
    ); // Draw the label, aligned with the checkbox

    // Draw the "Mother's Name" checkbox and label
    const officeCheckboxX = 140; // X position for the Mother's Name checkbox
    doc.rect(officeCheckboxX, locationcheckboxYn, checkboxSize, checkboxSize); // Draw the checkbox
    doc.text(
      "Offce",
      officeCheckboxX + checkboxSize + labelXOffset,
      locationcheckboxYn + labelYOffset
    ); // Draw the label, aligned with the checkbox

    // Auto-tick Residence
    drawCheckmark(resiCheckboxX, locationcheckboxYn);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const DetailboxesStartX = 63;
    const boxCountb = 27; // Number of boxes
    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Name of Office ",
      titleStartX,
      162, // Title Y position
      DetailboxesStartX,
      158, // Boxes Y position
      boxCountb,
      boxWidthn,
      boxHeightn,
      String(formData.OfficeName || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Flat/Room/ Door / Block No",
      titleStartX,
      167, // Title Y position
      DetailboxesStartX,
      163, // Boxes Y position
      boxCountb,
      boxWidthn,
      boxHeightn,
      String(formData.ResidentRoom || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Name of Premises/ Building/Village",
      titleStartX,
      171, // Title Y position
      DetailboxesStartX,
      168, // Boxes Y position
      boxCountb,
      boxWidthn,
      boxHeightn,
      String(formData.ResidentPremises || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Road/Street/ Lane/Post Office",
      titleStartX,
      176, // Title Y position
      DetailboxesStartX,
      173, // Boxes Y position
      boxCountb,
      boxWidthn,
      boxHeightn,
      String(formData.ResidentStreet || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Area / Locality / Taluka / Sub- Division",
      titleStartX,
      181, // Title Y position
      DetailboxesStartX,
      178, // Boxes Y position
      boxCountb,
      boxWidthn,
      boxHeightn,
      String(formData.ResidentArea || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );
    // Draw the "Middle Name" title and boxes
    drawTitleAndAlignedBoxes(
      "Town / City / District",
      titleStartX,
      186, // Title Y position
      DetailboxesStartX,
      183, // Boxes Y position
      boxCountb,
      boxWidthn,
      boxHeightn,
      String(formData.ResidentCity || "").toUpperCase(),
      doc,
      8,
      "normal",
      -2
    );

    // Shared dimensions and spacing
    const startXn = 10; // Starting X position for the first label/box group
    const startYn = 193; // Starting Y position for the boxes

    // Draw "State / Union Territory" label and box (large box)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("State / Union Territory", startXn, startYn + DatelabelYOffset); // Label for State
    doc.rect(startXn, startYn, 70, boxHeightn); // Large box for State
    // User-entered state in Kids Avenue
    doc.setFont("KidsAvenue", "normal");
    doc.text(String(formData.ResidentState || "").toUpperCase(), startXn + 2, startYn + boxHeightn / 2 + 2);
    doc.setFont("helvetica", "normal");

    // Draw "Pincode / Zip Code" label and 7 small boxes
    const pincodeStartX = startXn + 70 + 0; // Starting X position for Pincode
    doc.text("Pincode / Zip Code", pincodeStartX, startYn + DatelabelYOffset); // Label for Pincode
    // Ensure Pincode is exactly 7 characters (padding or truncation)
    const officePincode = (formData.ResidentPinCode || "").toString().padEnd(7, " ").substring(0, 7);

    for (let i = 0; i < 7; i++) { // Draw 7 small boxes for Pincode
      const currentX = pincodeStartX + i * (boxWidthn); // Adjust for consistent spacing
      doc.rect(pincodeStartX + i * (boxWidthn + 0), startYn, boxWidthn, boxHeightn);
      // User-entered pincode digits in Kids Avenue
      doc.setFont("KidsAvenue", "normal");
      doc.text(officePincode[i], currentX + boxWidthn / 2 - 1, startYn + boxHeightn / 2 + 2);
    }
    doc.setFont("helvetica", "normal");
    // Draw "Country Name" label and large box
    const countryStartX = pincodeStartX + 7 * (boxWidthn + 0) + 0; // Starting X for Country Name
    doc.text("Country Name", countryStartX, startYn + DatelabelYOffset); // Label for Country Name
    doc.rect(countryStartX, startYn, 70, boxHeightn); // Large box for Country Name
    doc.text("INDIA", countryStartX + 2, startYn + boxHeightn / 2 + 1);

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "8. If you desire to update your other address also, give required details In additional sheet.",
      10,
      201
    );
    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "9. Telephone Number & Email ID details",
      10,
      208
    );

    // Shared dimensions and spacing
    const CountrystartX = 65; // Starting X position for the first label/box group
    const CountrystartY = 207; // Starting Y position for the boxes

    // Draw "Country Code" label and 3 small boxes
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Country Code", CountrystartX, CountrystartY + DatelabelYOffset); // Label for Country Code

    const countryCode = "+91".padEnd(3, " ");
    for (let i = 0; i < 3; i++) {
      doc.rect(CountrystartX + i * (boxWidthn + 0), CountrystartY, boxWidthn, boxHeightn);
      doc.text(countryCode[i] || "", CountrystartX + i * (boxWidthn + 0) + 2, CountrystartY + 4);
    }

    // Draw "Area/STD Code" label and 7 small boxes
    const areaStartX = 60 + 3 * (boxWidthn + 5); // Starting X for Area/STD Code
    doc.text("Area/STD Code", areaStartX, CountrystartY + DatelabelYOffset); // Label for Area/STD Code
    const STDCode = String(formData.STDCode || "").padEnd(7, " ");
    for (let i = 0; i < 7; i++) {
      doc.rect(areaStartX + i * (boxWidthn + 0), CountrystartY, boxWidthn, boxHeightn);
      doc.setFont("KidsAvenue", "normal");
      doc.text(STDCode[i] || "", areaStartX + i * (boxWidthn + 0) + 2, CountrystartY + 4);
      doc.setFont("helvetica", "normal");
    }

    // Draw "Telephone/Mobile Number" label and 13 small boxes
    const telStartX = 60 + 7 * (boxWidthn + 5) + 5; // Starting X for Telephone/Mobile Number
    doc.text("Telephone/Mobile Number", telStartX, CountrystartY + DatelabelYOffset); // Label for Telephone/Mobile Number
    const MobileNo = String(formData.MobileNo || "").padEnd(12, " ");
    for (let i = 0; i < 12; i++) {
      doc.rect(telStartX + i * (boxWidthn + 0), CountrystartY, boxWidthn, boxHeightn);
      doc.setFont("KidsAvenue", "normal");
      doc.text(MobileNo[i] || "", telStartX + i * (boxWidthn + 0) + 2, CountrystartY + 4);
      doc.setFont("helvetica", "normal");
    }

    const emailStartX = 10; // Starting X position for the Email ID
    const emailStartY = 215; // Starting Y position for the box
    // Draw "Email ID" label and box
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Email ID", emailStartX, emailStartY + DatelabelYOffset); // Draw the label above the box
    doc.rect(63, 214, 134, 5); // Draw a single box for Email ID
    // User-entered email in Kids Avenue
    doc.setFont("KidsAvenue", "normal");
    doc.text(String(formData.EmailId || ""), 65, 217);
    doc.setFont("helvetica", "normal");

    // Shared dimensions and spacing
    const CountrystartXn = 63; // Starting X position for the first label/box group
    const CountrystartYn = 219; // Starting Y position for the boxes
    const DatelabelYOffset2 = 10;

    // Draw "AADHAAR number (if allotted)" label
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("10. AADHAAR number (if allotted)", 10, 213 + DatelabelYOffset2); // Label for AADHAAR

    // Draw "AADHAAR number (if allotted)" boxes and fill data
    const aadharEID = (formData.Aadhar_EID || "").toString().padEnd(12, " ").substring(0, 12);
    for (let i = 0; i < 12; i++) {
      doc.rect(CountrystartXn + i * (boxWidthn + 0), CountrystartYn, boxWidthn, boxHeightn);
      doc.setFont("KidsAvenue", "normal");
      doc.text(aadharEID[i], CountrystartXn + i * (boxWidthn + 0) + 1.5, CountrystartYn + 4);
      doc.setFont("helvetica", "normal");
    }

    // Draw the title "Name as per AADHAAR letter/card"
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Name as per AADHAAR letter/card", 10, 218 + DatelabelYOffset2); // Label for AADHAAR

    // Function to draw a single line of boxes
    const drawLineOfBoxes = (startXAdhar, startYAdhar, boxCountAdhar, boxWidthAdhar, boxHeightAdhar, spacing, doc) => {
      for (let i = 0; i < boxCountAdhar; i++) {
        // Draw each box with spacing
        doc.rect(startXAdhar + i * (boxWidthAdhar + spacing), startYAdhar, boxWidthAdhar, boxHeightAdhar);
      }
    };

    // General box dimensions and settings
    const boxCountAdhar = 27; // Number of boxes in each line
    const boxWidthAdhar = 5; // Width of each box
    const boxHeightAdhar = 5; // Height of each box
    const startXAdhar = 63; // Starting X position for the boxes
    let startYAdhar = 225; // Initial Y position for the first line

    // Draw three lines of boxes and fill with AadharName
    const aadharNameFull = (formData.AadharName || "").toUpperCase();
    const boxesPerLine = 27;
    for (let line = 0; line < 3; line++) {
      const lineText = aadharNameFull.substring(line * boxesPerLine, (line + 1) * boxesPerLine);
      for (let i = 0; i < boxesPerLine; i++) {
        const currentX = startXAdhar + i * boxWidthAdhar;
        const currentY = startYAdhar + line * boxHeightAdhar;
        doc.rect(currentX, currentY, boxWidthAdhar, boxHeightAdhar);
        if (lineText[i]) {
          doc.setFont("KidsAvenue", "normal");
          doc.text(lineText[i], currentX + 1.5, currentY + 4);
          doc.setFont("helvetica", "normal");
        }
      }
    }

    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "11 Mention other Permanent Account Numbers (PANs) inadvertently allotted to you",
      10,
      243
    );

    //Define shared variables for the layout
    const panLabelPrefix = "PAN"; // Text for each row
    const panBoxCount = 10; // Number of boxes per set
    const panLabelX = 10; // Starting X position for the "PAN" text
    const panBoxStartX = 8; // X position for the first box (space after "PAN")
    const panStartY = 245; // Y position for the row
    const panSetSpacing = 49; // Horizontal spacing between each set (for 4 sets)
    const boxHeightpanNew = 4;
    const boxWidthnpanNew = 4;
    // Loop to draw 4 sets in a single row
    for (let i = 0; i < 4; i++) {
      const currentSetX = panLabelX + i * panSetSpacing; // Start from X=10, then offset each set by panSetSpacing

      // Draw the "PAN1", "PAN2", "PAN3", or "PAN4" label for the current set
      const panLabel = panLabelPrefix + (i + 1); // Dynamically create "PAN1", "PAN2", "PAN3", "PAN4"
      doc.text(panLabel, currentSetX, panStartY + boxHeightpanNew / 2); // Align "PAN" to the center of the boxes

      // Draw 10 boxes for the current set
      for (let j = 0; j < panBoxCount; j++) {
        const boxX = currentSetX + panBoxStartX + j * boxWidthnpanNew; // X position for each box
        doc.rect(boxX, panStartY, boxWidthnpanNew, boxHeightpanNew); // Draw the box
      }
    }
    // Applicant Details Section
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "12 Verification",
      10,
      251
    );

    const startLineX = 10; // Starting X position
    const startLineY = 258; // Starting Y position
    const boxWidthLine = 70; // Width of the single box
    const boxHeightLine = 5; // Height of the box
    const textOffsetY = -2; // Offset to move text slightly upward
    const boxWidthLinesmall = 5;

    // Function to draw a single box
    function drawBox(x, y, width, height) {
      doc.rect(x, y, width, height); // Draw a rectangle (box)
    }

    // First line: "I/We," and box
    let currentY = startLineY;
    doc.text("I/We,", startLineX, currentY + textOffsetY);
    const aadharBoxX = startLineX + 8;
    const aadharBoxY = currentY - 6;
    drawBox(aadharBoxX, aadharBoxY, boxWidthLine, boxHeightLine); // Box after "I/We"
    // User-entered Aadhar name in Kids Avenue
    doc.setFont("KidsAvenue", "normal");
    doc.text(formData.AadharName || "", aadharBoxX + 2, aadharBoxY + 4);
    doc.setFont("helvetica", "normal");

    // Text "the applicant, in the capacity of" with box
    doc.text("the applicant, in the capacity of", startLineX + 1 + boxWidthLine + 10, currentY + textOffsetY);
    const capacityBoxX = startLineX + 3 + boxWidthLine + 50;
    const capacityBoxY = currentY - 6;
    drawBox(capacityBoxX, capacityBoxY, boxWidthLine, boxHeightLine); // Box after text
    if (formData.category && formData.category.trim() === "Individual") {
      doc.setFont("KidsAvenue", "normal");
      doc.text("Self", capacityBoxX + 2, capacityBoxY + 4);
      doc.setFont("helvetica", "normal");
    }

    // Next line: Declaration text
    currentY += 2; // Move to the next line
    doc.text(
      "do hereby declare that what is stated above is true to the best of my/our information and belief.",
      startLineX,
      currentY
    );

    // Third line: "I/We have enclosed" and box
    currentY += 4; // Move to the next line
    doc.text("I/We have enclosed", startLineX, currentY);
    const docBoxX = startLineX + 30;
    const docBoxY = currentY - 3;
    drawBox(docBoxX, docBoxY, boxWidthLinesmall, boxHeightLine); // Box for "number of documents"
    doc.text("4", docBoxX + 1.5, docBoxY + 4);
    doc.text("(number of documents) in support of proposed changes/corrections.", 50, currentY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text(
      "Place",
      10,
      270
    );

    doc.rect(30, 269, boxWidthLine, boxHeightLine);
    // User-entered Resident City in Kids Avenue
    doc.setFont("KidsAvenue", "normal");
    doc.text(formData.ResidentCity || "", 32, 273);
    doc.setFont("helvetica", "normal");

    // Draw the "Date" label
    doc.text("Date", 10, 280); // Inline label before the boxes

    const startYdate = 279; // Starting Y position
    const labelSpacing = 1; // Space between labels and boxes
    const xOffset = 20; // Shift boxes by 20 units to the right

    // Function to draw labeled boxes
    function drawLabeledBoxes(labels, startXdate, startYdate, boxWidthn, boxHeightn, dateStr) {
      labels.forEach((label, index) => {
        const boxX = startXdate + index * (boxWidthn + 0) + xOffset;
        doc.setFont("helvetica", "normal");
        doc.text(label, boxX + boxWidthn / 4, startYdate - labelSpacing);
        doc.rect(boxX, startYdate, boxWidthn, boxHeightn);
        if (dateStr && dateStr[index]) {
          doc.text(dateStr[index], boxX + boxWidthn / 2 - 1, startYdate + boxHeightn / 2 + 1);
        }
      });
    }

    // Prepare date string (DDMMYYYY) from backendSubmissionDate
    let formattedDate = "";
    const dateToUse = backendSubmissionDate || submissionDate; 
    if (dateToUse) {
      try {
        const d = new Date(dateToUse);
        if (!isNaN(d.getTime())) {
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = String(d.getFullYear());
          formattedDate = day + month + year;
        }
      } catch (e) {
        console.error("Error parsing submissionDate:", e);
      }
    }

    // Labels for date format
    const dateLabels = ["D", "D", "M", "M", "Y", "Y", "Y", "Y"];

    // Draw the labeled date boxes
    drawLabeledBoxes(dateLabels, startLineX, startYdate, 5, 5, formattedDate);

    // Right-side box properties
    const marginRight = 5; // Right margin of the PDF
    const rightBoxWidth = 65; // Wider box for better text display
    const rightBoxHeight = 20; // Smaller height for better alignment
    const rightBoxX = pageWidth - rightBoxWidth - marginRight; // Place box to the rightmost side
    const rightBoxY = 270; // Align Y position with the starting line

    // Draw the right-side box
    drawBox(rightBoxX, rightBoxY, rightBoxWidth, rightBoxHeight);

    if (signatureBase64) {
      doc.addImage(signatureBase64, "JPEG", rightBoxX + 1, rightBoxY + 1, rightBoxWidth - 2, rightBoxHeight - 2);
    }

    if (!signatureBase64) {
      // Add single-line text inside the right-side box (centered)
      doc.text(
        "Signature / Left Thumb Impression of Applicant",
        rightBoxX + 3,
        rightBoxY + rightBoxHeight / 1.1 // Center text vertically within the box
      );
    }

    doc.save("PAN_Correction_Application.pdf");

    // Then redirect (after small delay)
    setTimeout(() => {
      window.location.href = "/dashboard";
    }, 300);
  };
  return (
    <div className="kids-avenue-font">

      <form onSubmit={handleSubmitCorrectionPan}>
        <div className="card card-default">
          <div className="card-body">
            <div className="row">
              <div className="form-group col-md-3">
                <label htmlFor="Pan">Pan No <span style={{ color: "red" }}>*</span></label>
              </div>
              <div className="form-group col-md-3">
                <input type="text"
                  className="form-control"
                  name="PanNo"
                  value={formData.PanNo}
                  required
                  onChange={handleChange}
                  maxLength="10" />
                {errors.PanNo && <small style={{ color: "red" }}>{errors.PanNo}</small>}
              </div>
            </div>
          </div>
          <div className="border-top"></div>
          <div className="card-header">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="RangeCode">Status Of Applicant</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <select className="form-control" name="category" onChange={handleCategoryChange} value={formData.category}>
                    <option>Select Option  </option>
                    <option>Individual  </option>
                    <option>Trust</option>
                    <option>Hindu Undivided Family</option>
                    <option>Partnership Firm</option>
                    <option>Association Of Person</option>
                    <option>Body Of Individuals</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="applicant-section">
              <div className="form-check d-inline-block mb-3 col-md-2">
                <input className="form-check-input"
                  type="radio"
                  //checked="checked" value="" 
                  name="ApplicantCheckedOption"
                  value="Shri"
                  checked={formData.ApplicantCheckedOption === "Shri"}
                  onChange={handleCheckboxChange} />
                <label className="form-check-label" htmlFor="Shri">Shri</label>
              </div>

              <div className="form-check d-inline-block mb-3 col-md-2">
                <input className="form-check-input"
                  type="radio"
                  value="Smt"
                  name="ApplicantCheckedOption"
                  checked={formData.ApplicantCheckedOption === "Smt"}
                  onChange={handleCheckboxChange} />
                <label className="form-check-label" htmlFor="smt">Smt.</label>
              </div>

              <div className="form-check d-inline-block mb-3 col-md-2">
                <input className="form-check-input"
                  type="radio"
                  value="Kumari"
                  name="ApplicantCheckedOption"
                  checked={formData.ApplicantCheckedOption === "Kumari"}
                  onChange={handleCheckboxChange} />
                <label className="form-check-label" htmlFor="Kumari">Kumari</label>
              </div>
              <div className="form-check d-inline-block mb-3 col-md-4">
                <p>(please tick as applicable)</p>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <div className="form-group">
                  <label htmlFor="email">Name as per Aadhar <span style={{ color: "red" }}>*</span></label>
                  <input type="text"
                    className="form-control"
                    name="AadharName"
                    value={formData.AadharName}
                    required
                    onChange={handleChange} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3">
                <div className="form-group">
                  <label style={{ lineHeight: '35px' }}>Applicant Name <span style={{ color: "red" }}>*</span></label>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="Last name"
                    name="LastName"
                    value={formData.LastName}
                    required
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="First name"
                    name="FirstName"
                    value={formData.FirstName}
                    onChange={handleChange}
                    disabled={disableNames} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="Middle name"
                    name="MiddleName"
                    value={formData.MiddleName}
                    onChange={handleChange}
                    disabled={disableNames} />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="form-group">
                  <label htmlFor="mname">Name as you would like it, to be printed on the PAN card <span style={{ color: "red" }}>*</span></label>
                  <input type="text"
                    className="form-control"
                    placeholder=""
                    name="NewPanName"
                    value={formData.NewPanName}
                    required
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-12">
                <p>Whether mother is a single parent and you wish to apply for PAN by furnishing the name of your Yes No mother only?(please tick as applicable)</p>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3">
                <div className="form-group">
                  <label style={{ lineHeight: '35px' }}>Father's Name</label>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="Father's Last name"
                    name="FatherLastName"
                    value={formData.FatherLastName}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="First name"
                    name="FatherFirstName"
                    value={formData.FatherFirstName}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="Middle name"
                    name="FatherMiddleName"
                    value={formData.FatherMiddleName}
                    onChange={handleChange} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3">
                <div className="form-group">
                  <label style={{ lineHeight: '35px' }}>Mothrer's Name</label>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="Mother's Last name"
                    name="MotherLastName"
                    value={formData.MotherLastName}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="First name"
                    name="MotherFirstName"
                    value={formData.MotherFirstName}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="Middle name"
                    name="MotherMiddleName"
                    value={formData.MotherMiddleName}
                    onChange={handleChange} />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3">
                <div className="form-group">
                  <label>Date of Birth / Incorporation <span style={{ color: "red" }}>*</span></label>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="input-group mb-3">
                  <input type="date"
                    className="form-control"
                    data-mask="00/00/0000"
                    name="Dob"
                    required
                    value={formData.Dob}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <p style={{ fontSize: '90%' }}>ex. 99/99/9999</p>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3">
                <div className="form-group">
                  <label style={{ lineHeight: '35px' }}>Contact Details</label>
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="ISD code"
                    name="ISDCode"
                    value={formData.ISDCode}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="form-group">
                  <input type="text"
                    className="form-control"
                    placeholder="STD Code"
                    name="STDCode"
                    value={formData.STDCode}
                    onChange={handleChange} />
                </div>
              </div>
              <div className="col-sm-3">
                <div className="input-group mb-3">
                  <div className="input-group-prepend">
                    <span className="input-group-text mdi mdi-phone" id="basic-addon1" ></span>
                  </div>
                  <input type="text"
                    className="form-control"
                    data-mask="(999) 999-9999"
                    placeholder="Mobile No"
                    name="MobileNo"
                    value={formData.MobileNo}
                    onChange={handleChange}
                    required
                  />
                </div>
                <p style={{ fontSize: '90%' }}>ex. (999) 999-9999 <span style={{ color: "red" }}>*</span></p>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-6">
                <div className="form-group">
                  <label htmlFor="email">Email address <span style={{ color: "red" }}>*</span></label>
                  <input type="email"
                    className="form-control"
                    name="EmailId"
                    placeholder="Enter Email"
                    value={formData.EmailId}
                    required
                    onChange={handleChange} />
                  <span className="mt-2 d-block">We'll never share your email with anyone else.</span>
                </div>
              </div>

            </div>
            <div className="row">
              <div className="form-group col-md-3">
                <label htmlFor="PanDispatchState">Pan Card DispatchedState</label>
                <input type="text"
                  className="form-control"
                  name="PanDispatchState"
                  value={formData.PanDispatchState}
                  onChange={handleChange} />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="aadhar">Aadhaar/EID<span style={{ color: "red" }}>*</span></label>
                <input type="text"
                  className="form-control"
                  name="Aadhar_EID"
                  required
                  value={formData.Aadhar_EID}
                  onChange={handleChange}
                  maxLength="12" />
                {errors.AadharName && <small style={{ color: "red" }}>{errors.AadharName}</small>}

              </div>
              {/* <div className="form-group col-md-3">
                      <label htmlFor="RangeCode">RA Address </label>
                      <input type="text" 
                       className="form-control" 
                       name="RA_Address"                        
                       value={formData.RA_Address}
                       onChange={handleChange}/>
                    </div> */}
              {/* <div className="form-group col-md-3">
                      <label htmlFor="State">Proof of Identity</label>
                      <select className="form-control" name="IdentityProof" value={formData.IdentityProof} onChange={handleChange}>
                        <option>Select</option>
                        <option>Aadhar card</option>
                        <option>Passport</option>
                        <option>Driving Licence</option>
                        <option>Voter Card</option>
                      </select>
                    </div>
                    <div className="form-group col-md-3">
                      <label htmlFor="exampleFormControlSelect12">Proof of Address</label>
                      <select className="form-control" name="AddressProof" value={formData.AddressProof} onChange={handleChange}>
                        <option>select</option>
                        <option>Aadhar card</option>
                        <option>Passport</option>
                        <option>Voter Card</option>
                        <option>Electricity Bill (Not more than three Moths)</option>
                      </select>
                    </div>
                    <div className="form-group col-md-3">
                      <label htmlFor="exampleFormControlSelect12">Proof of DOB</label>
                      <select className="form-control" name="DobProof" value={formData.DobProof} onChange={handleChange}>
                        <option>select</option>
                        <option>Aadhar card</option>
                        <option>Birth Certificate</option>
                        <option>Passport</option>
                        <option>Voter Card</option>
                      </select>
                    </div> */}
            </div>
            <div>
              <div className="form-check d-inline-block mb-3 col-md-4">
                <input className="form-check-input"
                  type="checkbox"
                  name="Name Change/Correction"
                  checked={formData.ChangeResoneCheckedOption?.includes("Name Change/Correction") || false}
                  onChange={handleMultipleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="Shri">Name change/ Correction </label>
              </div>

              <div className="form-check d-inline-block mb-3 col-md-4">
                <input className="form-check-input"
                  type="checkbox"
                  name="Father's Name Change/Correction"
                  checked={formData.ChangeResoneCheckedOption?.includes("Father's Name Change/Correction") || false}
                  onChange={handleMultipleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="smt">Father's Name change/ Correction </label>
              </div>

              <div className="form-check d-inline-block mb-3 col-md-4">
                <input className="form-check-input"
                  type="checkbox"
                  name="Date of Birth Change"
                  checked={formData.ChangeResoneCheckedOption?.includes("Date of Birth Change") || false}
                  onChange={handleMultipleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="Kumari">Date of birth change</label>
              </div>
              <div className="form-check d-inline-block mb-3 col-md-4">
                <input className="form-check-input"
                  type="checkbox"
                  name="Photo"
                  checked={formData.ChangeResoneCheckedOption?.includes("Photo") || false}
                  onChange={handleMultipleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="Shri">Photo</label>
              </div>

              <div className="form-check d-inline-block mb-3 col-md-4">
                <input className="form-check-input"
                  type="checkbox"
                  name="Address"
                  checked={formData.ChangeResoneCheckedOption?.includes("Address") || false}
                  onChange={handleMultipleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="Kumari">Address</label>
              </div>

              <div className="form-check d-inline-block mb-3 col-md-4">
                <input className="form-check-input"
                  type="checkbox"
                  name="Signature"
                  checked={formData.ChangeResoneCheckedOption?.includes("Signature") || false}
                  onChange={handleMultipleCheckboxChange}
                />
                <label className="form-check-label" htmlFor="smt">Signature</label>
              </div>
            </div>
          </div>
          <div className="border-top"></div>
          <div className="card-header">
            <h2>Address</h2>
          </div>
          <div className="card-body">
            <ul className="nav nav-tabs mb-3" id="pills-tab2" role="tablist">
              <li className="nav-item">
                <button className="nav-link active" id="pills-home-tab" data-toggle="pill" href="#nav-tabs-home2" role="tab"
                  aria-controls="nav-tabs" aria-selected="true">
                  Resident</button>
              </li>
              {/* <li className="nav-item">
                      <button className="nav-link" id="nav-profile-tab" data-toggle="pill" href="#nav-profile2" role="tab"
                        aria-controls="nav-profile" aria-selected="false">
                        Office</button>
                    </li> */}
            </ul>
            <div className="tab-content mt-5" id="nav-tabContent">
              <div className="tab-pane fade show active" id="nav-tabs-home2" role="tabpanel" aria-labelledby="nav-home-tab">
                <h5 className="mb-3">Resident Address</h5>
                <div className="row">
                  <div className="form-group col-md-3">
                    <label htmlFor="ResidentRoom">Flat/ Room/ Bock No.</label>
                  </div>
                  <div className="form-group col-md-9">
                    <input type="text"
                      className="form-control"
                      name="ResidentRoom"
                      value={formData.ResidentRoom}
                      maxLength={25}
                      onChange={handleChange} />
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="AoType">Name of premises/ Bldg/ Village </label>
                  </div>
                  <div className="form-group col-md-8">
                    <input type="text"
                      className="form-control"
                      name="ResidentPremises"
                      maxLength={25}
                      value={formData.ResidentPremises}
                      onChange={handleChange} />
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="RangeCode">Road/ Street/ Lane/ Post</label>
                  </div>
                  <div className="form-group col-md-9">
                    <input type="text"
                      className="form-control"
                      name="ResidentStreet"
                      maxLength={25}
                      value={formData.ResidentStreet}
                      onChange={handleChange} />
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="AONo">Area/ Locality/ Taluka</label>
                  </div>
                  <div className="form-group col-md-3">
                    <input type="text"
                      className="form-control"
                      name="ResidentArea"
                      maxLength={25}
                      value={formData.ResidentArea}
                      onChange={handleChange} />
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="AONo">State/ Union Territory </label>
                  </div>
                  <div className="form-group col-md-3">
                    <select
                      name="ResidentState"
                      value={formData.ResidentState}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Select State</option>
                      {Object.keys(statesAndDistricts).map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="AONo">Town/ City/ District</label>
                  </div>
                  <div className="form-group col-md-3">
                    <select
                      name="ResidentCity"
                      value={formData.ResidentCity}
                      onChange={handleChange}
                      disabled={!formData.ResidentState}
                      className="form-control"
                    >
                      <option value="">Select District</option>
                      {formData.ResidentState &&
                        statesAndDistricts[formData.ResidentState].map((district) => (
                          <option key={district} value={district}>
                            {district}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group col-md-3">
                    <label htmlFor="AONo">Pin code </label>
                  </div>
                  <div className="form-group col-md-3">
                    <input type="number"
                      className="form-control"
                      name="ResidentPinCode"
                      inputMode="numeric"
                      pattern="\d{0,9}"
                      maxLength={6}
                      value={formData.ResidentPinCode}
                      onChange={handleChange} />
                  </div>
                </div>
                <div className="border-top"></div>
                <h5 className="mb-3 mt-4">Upload Document</h5>
                <div className="row">
                  <div className="col-md-3">
                    <input type="file"
                      className="form-control"
                      name="pdfDocument"
                      accept="application/pdf"
                      ref={pdfDocumentRef}
                      onChange={handleFileChange} />
                    <p htmlFor="AONo">Form and documents ( PDF only ) {!editId && <span style={{ color: "red" }}>*</span>}</p>
                  </div>
                  <div className="col-md-3">
                    <input type="file"
                      className="form-control"
                      name="photo"
                      accept=".jpg, .png"
                      ref={photoRef}
                      onChange={handleFileChange} />
                    <p htmlFor="AONo">Photo (Jpg and png only) {!editId && <span style={{ color: "red" }}>*</span>}</p>
                  </div>
                  <div className="col-md-3">
                    <input type="file"
                      className="form-control"
                      name="signature"
                      accept=".jpg, .png"
                      ref={signatureRef}
                      onChange={handleFileChange} />
                    <p htmlFor="AONo">Signature (Jpg and png only) {!editId && <span style={{ color: "red" }}>*</span>}</p>
                  </div>
                </div>
              </div>
              {/* <div className="tab-pane fade" id="nav-profile2" role="tabpanel" aria-labelledby="nav-profile-tab">
                    <h5 className="mb-3">Office Address</h5>
                    <div className="row">
                      <div className="form-group col-md-3">
                        <label htmlFor="OfficeName">Office Name</label>
                      </div>
                      <div className="form-group col-md-9">
                        <input type="text" 
                         className="form-control" 
                         name="OfficeName"
                         value={formData.OfficeName}
                         onChange={handleChange}/>
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="OfficeRoom">Flat/ Room/ Bock No.</label>
                      </div>
                      <div className="form-group col-md-9">
                        <input type="text" 
                         className="form-control" 
                         name="OfficeRoom"
                         value={formData.OfficeRoom}
                         onChange={handleChange}/>
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="AoType">Name of premises/ Bldg/ Village </label>
                      </div>
                      <div className="form-group col-md-8">
                        <input type="text" 
                         className="form-control" 
                         name="OfficePremises"
                         value={formData.OfficePremises}
                         onChange={handleChange} />
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="RangeCode">Road/ Street/ Lane/ Post</label>
                      </div>
                      <div className="form-group col-md-9">
                        <input type="text" 
                         className="form-control" 
                         name="OfficeStreet" 
                         value={formData.OfficeStreet}
                         onChange={handleChange} />
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="AONo">Area/ Locality/ Taluka</label>
                      </div>
                      <div className="form-group col-md-3">
                        <input type="text" 
                         className="form-control" 
                         name="OfficeArea" 
                         value={formData.OfficeArea}
                         onChange={handleChange}/>
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="AONo">Town/ City/ District</label>
                      </div>
                      <div className="form-group col-md-3">
                      <select
                          name="OfficeCity"
                          value={formData.OfficeCity}
                          onChange={handleChange}
                          disabled={!formData.OfficeState}
                           className="form-control"
                        >
                          <option value="">Select District</option>
                          {formData.OfficeState &&
                            statesAndDistricts[formData.OfficeState].map((district) => (
                              <option key={district} value={district}>
                                {district}
                              </option>
                            ))}
                        </select>
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="AONo">State/ Union Territory </label>
                      </div>
                      <div className="form-group col-md-3">
                      <select
                          name="OfficeState"
                          value={formData.OfficeState}
                          onChange={handleChange}
                           className="form-control"
                        >
                          <option value="">Select State</option>
                          {Object.keys(statesAndDistricts).map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group col-md-3">
                        <label htmlFor="AONo">Pin code </label>
                      </div>
                      <div className="form-group col-md-3">
                        <input type= "number" 
                         className="form-control" 
                         name="OfficePinCode" 
                         nputMode="numeric" 
                         pattern="\d{0,9}"
                         maxLength={6} 
                         value={formData.OfficePinCode}
                         onChange={handleChange}/>
                      </div>
                    </div>
                  </div> */}
            </div>
            <div className="form-footer pt-3 text-center">
              {!editId && <p>Note: Form Submition Charges Rs.120</p>}
              <button type="submit" className="btn btn-primary btn-pill">
                {editId ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </form>
      {showConfirmModal && (
        <div className="modal-backdrop" style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
        }}>
          <div style={{
            background: "#fff", padding: 24, borderRadius: 8, minWidth: 300, textAlign: "center"
          }}>
            <h5>Do you want to submit?</h5>
            <p>Press Confirm to submit or Stay to remain on this page.</p>
            <button className="btn btn-secondary mr-2" onClick={handleStay}>Stay</button>
            <button className="btn btn-primary" onClick={handleConfirm}>Confirm</button>
          </div>
        </div>
      )}

    </div>

  );
};
export default CorrectionPanApplication;